import { useState, useEffect, useRef } from "react";
import { getCachedDisplayImage, setCachedDisplayImage } from "../utils/displayImageCache";
import { supabase } from "../utils/supabaseClient";

interface UseDisplayImageProps {
  url: string;
}

const MAX_MEMORY_CACHE_ITEMS = 60;
const memoryCache = new Map<string, Blob>();
const inFlightFetches = new Map<string, Promise<Blob>>();

const rememberImage = (url: string, blob: Blob) => {
  memoryCache.delete(url);
  memoryCache.set(url, blob);

  if (memoryCache.size > MAX_MEMORY_CACHE_ITEMS) {
    const oldestUrl = memoryCache.keys().next().value;
    if (oldestUrl) memoryCache.delete(oldestUrl);
  }
};

const fetchImageBlob = async (url: string): Promise<Blob> => {
  const existingFetch = inFlightFetches.get(url);
  if (existingFetch) return existingFetch;

  const fetchPromise = (async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    return response.blob();
  })();

  inFlightFetches.set(url, fetchPromise);

  try {
    return await fetchPromise;
  } finally {
    inFlightFetches.delete(url);
  }
};

export function useDisplayImage({ url }: UseDisplayImageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const currentBlobUrlRef = useRef<string | null>(null);

  const cleanup = () => {
    // Cleanup previous blob URL to prevent memory leaks
    if (currentBlobUrlRef.current) {
      URL.revokeObjectURL(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      if (!url) {
        setLoading(false);
        setError('Missing URL');
        return;
      }

      setLoading(true);
      setError(null);
      cleanup();
      setImageUrl(null);

      try {
        const memoryImage = memoryCache.get(url);
        if (memoryImage) {
          if (mounted) {
            const localUrl = URL.createObjectURL(memoryImage);
            currentBlobUrlRef.current = localUrl;
            setImageUrl(localUrl);
            setLoading(false);
          }
          return;
        }

        // Check IndexedDB first
        const cached = await getCachedDisplayImage(url);

        // If cached, use it
        if (cached) {
          rememberImage(url, cached.blob);
          if (mounted) {
            const localUrl = URL.createObjectURL(cached.blob);
            currentBlobUrlRef.current = localUrl;
            setImageUrl(localUrl);
            setLoading(false);
          }
          return;
        }

        // Fetch from server with authentication
        const blob = await fetchImageBlob(url);
        rememberImage(url, blob);

        if (mounted) {
          const blobUrl = URL.createObjectURL(blob);
          currentBlobUrlRef.current = blobUrl;
          setImageUrl(blobUrl);
          setLoading(false);
        }

        setCachedDisplayImage(url, blob).catch(() => {});
        
      } catch (err) {
        console.error("Display image load error:", err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load image');
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
      cleanup();
    };
  }, [url]);

  return { imageUrl, loading, error };
} 